import { useCallback, useEffect, useRef } from "react";
import { useChatStore } from "@metalmind-ab/mmagent-chat-ui";
import type { BackgroundJob } from "@metalmind-ab/mmagent-chat-ui";
import type { DocumentCardCompletionDiagnostic } from "../../../services/frontendErrorReporting";

interface UseBackgroundJobProgressResilienceOptions {
  apiBaseUrl: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  reportDocumentCardCompletionDiagnostic?: (
    details: DocumentCardCompletionDiagnostic,
  ) => void;
}

interface JobStreamEvent {
  type: string;
  skill_name?: string;
  progress?: number;
  progress_message?: string;
  result?: Record<string, unknown>;
  error?: string;
  user_display?: BackgroundJob["userDisplay"];
  llm_context?: string;
}

const JOB_STREAM_RECONNECT_DELAY_MS =
  process.env.NODE_ENV === "test" ? 0 : 2000;
const JOB_STREAM_STALE_TIMEOUT_WITHOUT_KEEPALIVE_MS =
  process.env.NODE_ENV === "test" ? 25 : 300000;
const JOB_STREAM_STALE_TIMEOUT_WITH_KEEPALIVE_MS =
  process.env.NODE_ENV === "test" ? 25 : 45000;
const DOCUMENT_CARD_COMPLETION_DIAGNOSTIC_DELAY_MS = 5000;
class JobStreamStaleError extends Error {
  constructor() {
    super("Background job stream went stale");
    this.name = "JobStreamStaleError";
  }
}

const isRunningJob = (job: BackgroundJob | undefined) =>
  job?.status === "pending" || job?.status === "running";

const updateToolExecutionForJob = (
  jobId: string,
  status: "completed" | "failed",
  event: JobStreamEvent,
) => {
  const store = useChatStore.getState();
  for (const message of store.messages) {
    for (const execution of message.toolExecutions ?? []) {
      if (String(execution.result?.job_id ?? "") !== jobId) continue;

      store.updateToolExecutionInMessage(message.id, execution.id, {
        status: status === "completed" ? "completed" : "error",
        result: {
          ...execution.result,
          ...event.result,
          job_id: jobId,
          success: status === "completed",
          ...(event.error ? { error: event.error } : {}),
        },
        error: status === "failed" ? event.error : undefined,
      });
    }
  }
};

const getDocumentCardCompletionDiagnostic = (
  jobId: string,
): DocumentCardCompletionDiagnostic & { complete: boolean } => {
  const store = useChatStore.getState();
  const job = store.backgroundJobs.find((entry) => entry.jobId === jobId);
  let matchingExecution: { id: string; status?: string } | undefined;
  for (const message of store.messages) {
    matchingExecution = message.toolExecutions?.find(
      (execution: any) =>
        (execution.name === "generate_document" ||
          execution.name === "generate_presentation") &&
        String(execution.result?.job_id ?? "") === jobId,
    );
    if (matchingExecution) break;
  }
  const storeState = job?.status ?? "missing";
  const executionStatus = matchingExecution?.status ?? "missing";
  return {
    jobId,
    ...(matchingExecution ? { toolExecutionId: matchingExecution.id } : {}),
    executionStatus,
    executionFound: Boolean(matchingExecution),
    storeState,
    complete: storeState === "completed" && executionStatus === "completed",
  };
};

// Inline index jobs are not registered with the Hub, so subscribing to their
// per-job stream returns 403. The API identifies these by the `index-` job id
// prefix; the existing skill-name check also covers inline pseudo-jobs that
// are sent only on the originating chat stream.
const INLINE_PSEUDO_JOB_SKILL_NAMES = new Set(["index_session_files"]);

export const isStreamableJob = (job: BackgroundJob | undefined) =>
  job !== undefined &&
  isRunningJob(job) &&
  !job.jobId.startsWith("index-") &&
  !INLINE_PSEUDO_JOB_SKILL_NAMES.has(job.skillName ?? "");

/**
 * ChatProvider owns the tool row, while this hook owns the sole per-job SSE
 * subscription. Seed its store entry when ChatProvider deliberately skips its
 * own stream so this hook can subscribe and reconcile the terminal state.
 */
export function registerBackgroundJobForResilience(job: BackgroundJob) {
  if (!isStreamableJob(job)) return;

  const store = useChatStore.getState();
  if (store.backgroundJobs.some((entry) => entry.jobId === job.jobId)) return;
  store.addBackgroundJob(job);
}

const waitForReconnect = (signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal.aborted || JOB_STREAM_RECONNECT_DELAY_MS === 0) {
      resolve();
      return;
    }
    const timeout = window.setTimeout(resolve, JOB_STREAM_RECONNECT_DELAY_MS);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timeout);
        resolve();
      },
      { once: true },
    );
  });

const readWithStaleTimeout = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal: AbortSignal,
  staleTimeoutMs: number,
) => {
  let timeout: number | undefined;
  let abortHandler: (() => void) | undefined;

  try {
    return await Promise.race([
      reader.read(),
      new Promise<never>((_, reject) => {
        timeout = window.setTimeout(
          () => reject(new JobStreamStaleError()),
          staleTimeoutMs,
        );
        abortHandler = () => reject(new DOMException("Aborted", "AbortError"));
        signal.addEventListener("abort", abortHandler, { once: true });
      }),
    ]);
  } finally {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
    if (abortHandler) {
      signal.removeEventListener("abort", abortHandler);
    }
  }
};

/**
 * App-level subscriber for background-job progress streams.
 *
 * `mmagent-chat-ui` subscribes to `/chat/jobs/{job_id}/stream`, but its stream
 * reader exits silently if the SSE connection closes without `[DONE]` or a
 * terminal job event. A long-lived SSE socket can also go stale during sparse
 * progress windows, leaving the store at the last observed progress message
 * (for example `1/13`) while the backend keeps working. ChatProvider delegates
 * streamable jobs to this hook so each job has one queue consumer. This hook
 * reconnects on clean EOF or no-event staleness without terminal state. The
 * default stale threshold is intentionally above observed sparse progress
 * gaps; if the server starts emitting keepalive frames, this hook tightens the
 * threshold for faster dead socket detection.
 */
export function useBackgroundJobProgressResilience({
  apiBaseUrl,
  getAuthHeaders,
  reportDocumentCardCompletionDiagnostic,
}: UseBackgroundJobProgressResilienceOptions) {
  const backgroundJobs = useChatStore((state) => state.backgroundJobs);
  const updateBackgroundJob = useChatStore(
    (state) => state.updateBackgroundJob,
  );

  const getAuthHeadersRef = useRef(getAuthHeaders);
  const updateBackgroundJobRef = useRef(updateBackgroundJob);
  const backgroundJobsRef = useRef(backgroundJobs);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const documentCompletionTimersRef = useRef<Map<string, number>>(new Map());
  const reportedDocumentCompletionsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    getAuthHeadersRef.current = getAuthHeaders;
  }, [getAuthHeaders]);

  useEffect(() => {
    updateBackgroundJobRef.current = updateBackgroundJob;
  }, [updateBackgroundJob]);

  useEffect(() => {
    backgroundJobsRef.current = backgroundJobs;
  }, [backgroundJobs]);

  const getJob = useCallback(
    (jobId: string) =>
      backgroundJobsRef.current.find((job) => job.jobId === jobId),
    [],
  );

  const applyJobEvent = useCallback(
    (jobId: string, event: JobStreamEvent): boolean => {
      switch (event.type) {
        case "job_progress":
          updateBackgroundJobRef.current(jobId, {
            progress: event.progress ?? 0,
            progressMessage: event.progress_message,
          });
          return false;
        case "job_completed":
          updateBackgroundJobRef.current(jobId, {
            status: "completed",
            progress: 1,
            completedAt: new Date(),
            ...(event.result ? { result: event.result } : {}),
          });
          updateToolExecutionForJob(jobId, "completed", event);
          const job = getJob(jobId);
          if (
            reportDocumentCardCompletionDiagnostic &&
            !reportedDocumentCompletionsRef.current.has(jobId) &&
            (job?.skillName === "generate_document" ||
              job?.skillName === "generate_presentation" ||
              event.skill_name === "generate_document" ||
              event.skill_name === "generate_presentation")
          ) {
            const previousTimer =
              documentCompletionTimersRef.current.get(jobId);
            if (previousTimer !== undefined) window.clearTimeout(previousTimer);
            const timer = window.setTimeout(() => {
              documentCompletionTimersRef.current.delete(jobId);
              const diagnostic = getDocumentCardCompletionDiagnostic(jobId);
              if (!diagnostic.complete) {
                reportedDocumentCompletionsRef.current.add(jobId);
                const { complete: _complete, ...details } = diagnostic;
                reportDocumentCardCompletionDiagnostic(details);
              }
            }, DOCUMENT_CARD_COMPLETION_DIAGNOSTIC_DELAY_MS);
            documentCompletionTimersRef.current.set(jobId, timer);
          }
          return true;
        case "job_failed":
          updateBackgroundJobRef.current(jobId, {
            status: "failed",
            progress: event.progress ?? 0,
            completedAt: new Date(),
          });
          updateToolExecutionForJob(jobId, "failed", event);
          return true;
        case "error":
          return false;
        default:
          return false;
      }
    },
    [getJob, reportDocumentCardCompletionDiagnostic],
  );

  const consumeJobStream = useCallback(
    async (
      jobId: string,
      response: Response,
      signal: AbortSignal,
    ): Promise<boolean> => {
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let sawTerminal = false;
      let staleTimeoutMs = JOB_STREAM_STALE_TIMEOUT_WITHOUT_KEEPALIVE_MS;

      while (true) {
        let readResult: ReadableStreamReadResult<Uint8Array>;
        try {
          readResult = await readWithStaleTimeout(
            reader,
            signal,
            staleTimeoutMs,
          );
        } catch (error) {
          if (error instanceof JobStreamStaleError) {
            await reader.cancel().catch(() => undefined);
          }
          throw error;
        }

        const { done, value } = readResult;
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith(":")) {
            staleTimeoutMs = JOB_STREAM_STALE_TIMEOUT_WITH_KEEPALIVE_MS;
            continue;
          }
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).replace(/^ /, "").trim();
          if (!data) continue;
          if (data === "[DONE]") {
            return sawTerminal;
          }
          try {
            const event = JSON.parse(data) as JobStreamEvent;
            if (event.type === "heartbeat" || event.type === "keepalive") {
              staleTimeoutMs = JOB_STREAM_STALE_TIMEOUT_WITH_KEEPALIVE_MS;
              continue;
            }
            sawTerminal = applyJobEvent(jobId, event) || sawTerminal;
          } catch {
            // Ignore malformed frames; the authoritative stream may still send
            // subsequent valid progress events.
          }
        }
      }

      return sawTerminal;
    },
    [applyJobEvent],
  );

  const subscribeJob = useCallback(
    (job: BackgroundJob) => {
      if (controllersRef.current.has(job.jobId)) return;

      const controller = new AbortController();
      controllersRef.current.set(job.jobId, controller);

      (async () => {
        while (!controller.signal.aborted && isRunningJob(getJob(job.jobId))) {
          try {
            const headers = await getAuthHeadersRef.current();
            const response = await fetch(
              `${apiBaseUrl}/chat/jobs/${job.jobId}/stream`,
              {
                method: "GET",
                headers: {
                  Accept: "text/event-stream",
                  ...headers,
                },
                signal: controller.signal,
              },
            );
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }

            const sawTerminal = await consumeJobStream(
              job.jobId,
              response,
              controller.signal,
            );
            if (sawTerminal || !isRunningJob(getJob(job.jobId))) {
              return;
            }
            console.warn(
              `[IDA Chat] Background job stream closed without terminal event; reconnecting job=${job.jobId}`,
            );
          } catch (error) {
            if (controller.signal.aborted) return;
            if (error instanceof JobStreamStaleError) {
              console.warn(
                `[IDA Chat] Background job stream stale; reconnecting job=${job.jobId}`,
              );
            } else {
              console.warn(
                `[IDA Chat] Background job stream error; reconnecting job=${job.jobId}:`,
                error,
              );
            }
          }
          await waitForReconnect(controller.signal);
        }
      })().finally(() => {
        if (controllersRef.current.get(job.jobId) === controller) {
          controllersRef.current.delete(job.jobId);
        }
      });
    },
    [apiBaseUrl, consumeJobStream, getJob],
  );

  useEffect(() => {
    for (const job of backgroundJobs) {
      if (isStreamableJob(job)) {
        subscribeJob(job);
      } else {
        const controller = controllersRef.current.get(job.jobId);
        if (controller) {
          controller.abort();
          controllersRef.current.delete(job.jobId);
        }
      }
    }

    for (const [jobId, controller] of controllersRef.current.entries()) {
      if (!isRunningJob(backgroundJobs.find((job) => job.jobId === jobId))) {
        controller.abort();
        controllersRef.current.delete(jobId);
      }
    }
  }, [backgroundJobs, subscribeJob]);

  useEffect(
    () => () => {
      for (const controller of controllersRef.current.values()) {
        controller.abort();
      }
      controllersRef.current.clear();
      for (const timer of documentCompletionTimersRef.current.values()) {
        window.clearTimeout(timer);
      }
      documentCompletionTimersRef.current.clear();
    },
    [],
  );
}
